<template lang="pug">
v-dialog(
  v-model="showDialog"
  persistent
  max-width="500"
)
  v-card
    v-card-title.headline.warning.white--text
      v-icon(left color="white") timer_off
      span ⚠️ Aviso de Inatividade

    v-card-text.pt-4
      v-alert(
        type="warning"
        outlined
        prominent
      )
        .text-h6.mb-2 Você será desconectado em breve!
        p.mb-1
          | Você está inativo há algum tempo e será desconectado automaticamente em:

        .text-center.my-4
          .display-2.warning--text
            | {{ secondsRemaining }}
          .text-caption segundos

        p.mb-0.text-caption
          v-icon(small left) info
          | Mova o mouse ou pressione qualquer tecla para continuar conectado.

      v-progress-linear(
        :value="progressValue"
        color="warning"
        height="8"
        striped
      )

    v-card-actions
      v-spacer
      v-btn(
        color="primary"
        @click="stayConnected"
        large
      )
        v-icon(left) check
        | Continuar Conectado
</template>

<script>
/**
 * 🚪 MODAL DE AVISO DE INATIVIDADE
 *
 * Exibe um aviso quando o usuário está próximo de ser desconectado por inatividade.
 * Escuta eventos globais do ActivityDetector.
 *
 * @component InactivityWarningModal
 */
export default {
  name: 'InactivityWarningModal',

  data() {
    return {
      showDialog: false,
      secondsRemaining: 30,
      initialSeconds: 30,
      countdownInterval: null
    }
  },

  computed: {
    /**
     * 📊 Valor da barra de progresso (0-100)
     */
    progressValue() {
      return (this.secondsRemaining / this.initialSeconds) * 100
    }
  },

  mounted() {
    // 👂 Escutar eventos do ActivityDetector
    window.addEventListener('INACTIVITY_WARNING', this.handleInactivityWarning)
    window.addEventListener('INACTIVITY_CANCELLED', this.handleInactivityCancelled)
    window.addEventListener('AUTO_LOGOUT', this.handleAutoLogout)
  },

  beforeDestroy() {
    // 🧹 Limpar listeners e interval
    window.removeEventListener('INACTIVITY_WARNING', this.handleInactivityWarning)
    window.removeEventListener('INACTIVITY_CANCELLED', this.handleInactivityCancelled)
    window.removeEventListener('AUTO_LOGOUT', this.handleAutoLogout)

    if (this.countdownInterval) {
      clearInterval(this.countdownInterval)
    }
  },

  methods: {
    /**
     * ⚠️ Handler para aviso de inatividade
     */
    handleInactivityWarning(event) {
      const { secondsRemaining, message } = event.detail

      console.log('⚠️ Modal: Aviso de inatividade recebido', { secondsRemaining, message })

      this.secondsRemaining = secondsRemaining
      this.initialSeconds = secondsRemaining
      this.showDialog = true

      // Iniciar contagem regressiva
      this.startCountdown()

      // Mostrar notificação também
      if (this.$snotify) {
        this.$snotify.warning(message, {
          timeout: 5000,
          showProgressBar: true,
          closeOnClick: false,
          pauseOnHover: true
        })
      }
    },

    /**
     * ✅ Handler para cancelamento de inatividade (usuário voltou a ser ativo)
     */
    handleInactivityCancelled() {
      console.log('✅ Modal: Inatividade cancelada')
      this.closeModal()

      if (this.$snotify) {
        this.$snotify.success('Atividade detectada! Você continuará conectado.', {
          timeout: 3000,
          showProgressBar: true
        })
      }
    },

    /**
     * 🚪 Handler para logout automático
     */
    handleAutoLogout(event) {
      console.log('🚪 Modal: Logout automático em andamento')
      this.closeModal()

      const { message } = event.detail
      if (this.$snotify) {
        this.$snotify.error(message, {
          timeout: 5000,
          showProgressBar: true
        })
      }
    },

    /**
     * ⏱️ Iniciar contagem regressiva
     */
    startCountdown() {
      // Limpar interval anterior se existir
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
      }

      // Criar novo interval para atualizar a cada segundo
      this.countdownInterval = setInterval(() => {
        this.secondsRemaining--

        // Fechar modal quando chegar a 0
        if (this.secondsRemaining <= 0) {
          this.closeModal()
        }
      }, 1000)
    },

    /**
     * 🔄 Continuar conectado (simular atividade)
     */
    stayConnected() {
      console.log('✅ Usuário clicou em continuar conectado')

      // Simular atividade para resetar o timer
      document.dispatchEvent(new MouseEvent('click'))

      // Fechar modal
      this.closeModal()

      if (this.$snotify) {
        this.$snotify.success('Você continuará conectado!', {
          timeout: 2000,
          showProgressBar: true
        })
      }
    },

    /**
     * 🚫 Fechar modal
     */
    closeModal() {
      this.showDialog = false

      if (this.countdownInterval) {
        clearInterval(this.countdownInterval)
        this.countdownInterval = null
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.v-card__title {
  word-break: break-word;
}

.display-2 {
  font-weight: bold;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.1);
}
</style>
